import { GetStaticPaths, GetStaticPropsContext } from 'next'
import { useRouter } from 'next/router'
import { useContentfulLiveUpdates } from '@contentful/live-preview/react'
import dynamic from 'next/dynamic'

import { PgPageProps } from '../[[...slug]]'
import { FlooringWeaveProvider } from 'context'
import { Layout } from 'components/layouts'
import { environment } from '@config/environment'
import { getAllProductSlugs, getProduct, getPageBySlug, getHeaderAndFooter } from 'services/contentful'
import {
  getStartingAtPrices,
  getWeaveParameters,
  getWeaveWithAddonPrices,
  ObjFlooringWeave,
  WeaveParametersResponse,
  WeaveStartingAtPriceResponse,
} from '@services/prices'
import { loadFallback } from 'utils'
import { ContentfulObjFlooringWeave, ContentfulObjSeo, ContentType, FurniturePrices, SiteDomain } from 'types'
import { floorRugsRedirects } from 'floorRedirects'
import { getRoundedUpRollWidth } from '@components/rug-builder/order-rug/steps/customize-rug-types/utils'

const isFlooringSite = environment.siteDomain === SiteDomain.FLOORING
const isSisalcarpetSite = environment.siteDomain === SiteDomain.SISALCARPET
const isSynsisalSite = environment.siteDomain === SiteDomain.SYNSISAL

const TmSynsisalProductDetail = dynamic(() =>
  import('@templates/tm-product-detail/tm-synsisal-product-detail').then((mod) => mod.TmSynsisalProductDetail),
)
const TmFlooringProductDetail = dynamic(() =>
  import('@templates/tm-product-detail/tm-flooring-product-detail').then((mod) => mod.TmFlooringProductDetail),
)

export type PgProductProps = PgPageProps & {
  product: ObjFlooringWeave & {
    parameters: WeaveParametersResponse
    startingAtPrice: WeaveStartingAtPriceResponse
  }
  prices: FurniturePrices
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (environment.siteDomain === SiteDomain.FURNITURE) return { paths: [], fallback: false }

  const slugs = await getAllProductSlugs()
  const normalizeSlugs = slugs.map((slug) => slug.join('/'))

  // eslint-disable-next-line
  console.log('\nGenerating static pages for the following weave slugs:\n', normalizeSlugs)

  return {
    paths: normalizeSlugs.map((slug) => ({
      params: { slug },
    })),
    fallback: true,
  }
}

export const getStaticProps = async ({ params, preview }: GetStaticPropsContext<{ slug: string }>) => {
  const productSlug = params?.slug

  if (productSlug && floorRugsRedirects[productSlug]) {
    const newUrl = floorRugsRedirects[productSlug]

    return {
      redirect: {
        destination: newUrl,
        permanent: true,
      },
    }
  }

  const isPreview = preview ?? false

  const product = (await getProduct(productSlug, preview)) as ContentfulObjFlooringWeave

  const lowerCaseKeywords = product && product.keywords?.map((keyword) => keyword.toLowerCase())
  const isSynsisalProduct = isSynsisalSite && lowerCaseKeywords?.includes('synsisal')

  if (isSynsisalSite && !isSynsisalProduct) {
    return { notFound: true }
  }

  const productWithAddonPrices =
    isFlooringSite || isSisalcarpetSite || isSynsisalSite
      ? await getWeaveWithAddonPrices(product as ContentfulObjFlooringWeave)
      : null

  const page = await getPageBySlug('/', isPreview)
  const parameters = await getWeaveParameters({ weave: product?.CONTENTFUL_ID ?? '' })
  const normalizedParameters = { ...parameters, roll_width: getRoundedUpRollWidth(parameters) }
  const startAtPrice = await getStartingAtPrices({ weaveReference: product?.CONTENTFUL_ID ?? '' })

  if (page) {
    const { footerContentful, headerContentful } = await getHeaderAndFooter(page, isPreview)

    if (product && (isFlooringSite || isSisalcarpetSite || isSynsisalSite)) {
      return {
        props: {
          ...page,
          header: headerContentful,
          product: {
            ...productWithAddonPrices,
            parameters: normalizedParameters ?? null,
            startingAtPrice: startAtPrice ?? null,
          },
          footer: footerContentful,
          isPreview,
          notFound: !page,
        },
        revalidate: 5,
      }
    }
  }

  return { notFound: true }
}

const ProductPage = (props: PgProductProps | { notFound: boolean }) => {
  const page = props as PgProductProps
  const { isPreview, header, footer, product } = page
  const updatedProduct = useContentfulLiveUpdates((props as PgProductProps).product) || product
  const router = useRouter()

  if (router.isFallback) return loadFallback()

  const weaveSeo: ContentfulObjSeo = {
    name: `${product.weaveName} SEO`,
    CONTENT_TYPE: ContentType.OBJ_SEO,
    CONTENTFUL_ID: `${product.CONTENTFUL_ID}-seo`,
    UPDATED_AT: product.UPDATED_AT,
    metaTitle: product.weaveName,
    metaDescription: product.metaDescription || product.weaveName,
    metaKeywords: product.keywords,
    ...(product.images ? { socialShareImage: product.images } : {}),
  }

  return (
    <Layout slug={`rugs/${product.weaveNameSlug}`} isPreview={isPreview} seo={weaveSeo} header={header} footer={footer}>
      <FlooringWeaveProvider>
        {environment.siteDomain === SiteDomain.SYNSISAL ? (
          <TmSynsisalProductDetail product={updatedProduct} />
        ) : (
          <TmFlooringProductDetail product={updatedProduct} />
        )}
      </FlooringWeaveProvider>
    </Layout>
  )
}

export default ProductPage
